import React, { useState, useEffect, useRef } from 'react';
import { 
  MapPinIcon, 
  MagnifyingGlassIcon, 
  DocumentArrowDownIcon,
  ArrowLeftIcon,
  XMarkIcon,
  GlobeAltIcon,
  StarIcon,
  PhoneIcon,
  MapIcon,
  ArrowPathIcon
} from '@heroicons/react/24/outline';
import { fieldDayPlannerService } from '../firebase/services';
import { useNavigate } from 'react-router-dom';

/* global google */

const FieldDayPlanner = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    startLocation: '',
    endLocation: '',
    waypoints: '',
    radius: '1000'
  });
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [availableCategories, setAvailableCategories] = useState([]);
  
  const [partners, setPartners] = useState([]);
  const [filteredPartners, setFilteredPartners] = useState([]);
  const [route, setRoute] = useState(null);
  const [loading, setLoading] = useState(false);
  const [map, setMap] = useState(null);
  const [directionsService, setDirectionsService] = useState(null);
  const [directionsRenderer, setDirectionsRenderer] = useState(null);
  const [visitStatus, setVisitStatus] = useState({});
  const [visitNotes, setVisitNotes] = useState({});
  const [selectedPartner, setSelectedPartner] = useState(null);
  const [showPartnerModal, setShowPartnerModal] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [partnersPerPage] = useState(25);
  const [routeMarkers, setRouteMarkers] = useState([]);
  const [useGooglePlaces, setUseGooglePlaces] = useState(true);
  const [googlePlacesService, setGooglePlacesService] = useState(null);
  const [currentPlan, setCurrentPlan] = useState(null);
  const [planName, setPlanName] = useState('');
  
  const mapRef = useRef(null);
  const startLocationRef = useRef(null);
  const endLocationRef = useRef(null);

  // Initialize Google Maps
  useEffect(() => {
    const initMap = () => {
      try {
        if (window.google && window.google.maps && mapRef.current) {
          const mapInstance = new window.google.maps.Map(mapRef.current, {
            center: { lat: 20.5937, lng: 78.9629 }, // Center of India
            zoom: 5,
            styles: [
              {
                featureType: 'poi',
                elementType: 'labels',
                stylers: [{ visibility: 'off' }]
              }
            ]
          });

          const directionsServiceInstance = new window.google.maps.DirectionsService();
          const directionsRendererInstance = new window.google.maps.DirectionsRenderer({
            map: mapInstance,
            suppressMarkers: true
          });

          const placesServiceInstance = new window.google.maps.places.PlacesService(mapInstance);
          
          setMap(mapInstance);
          setDirectionsService(directionsServiceInstance);
          setDirectionsRenderer(directionsRendererInstance);
          setGooglePlacesService(placesServiceInstance);
        } else {
          console.warn('Google Maps API not fully loaded yet');
        }
      } catch (error) {
        console.error('Error initializing Google Maps:', error);
      }
    };

    if (window.google && window.google.maps) {
      initMap();
    } else {
      const timer = setInterval(() => {
        if (window.google && window.google.maps) {
          initMap();
          clearInterval(timer);
        }
      }, 100);
      
      // Timeout after 10 seconds
      const timeout = setTimeout(() => {
        clearInterval(timer);
        console.warn('Failed to load Google Maps API. Please check your internet connection.');
      }, 10000);
      
      return () => {
        clearInterval(timer);
        clearTimeout(timeout);
      };
    }
  }, []);

  // Initialize Google Places Autocomplete
  useEffect(() => {
    const initAutocomplete = () => {
      try {
        if (window.google && window.google.maps && window.google.maps.places && 
            startLocationRef.current && endLocationRef.current) {
          // Initialize autocomplete for start location
          const startAutocomplete = new google.maps.places.Autocomplete(startLocationRef.current, {
            types: ['geocode', 'establishment'],
            componentRestrictions: { country: 'IN' } // Restrict to India
          });

          // Initialize autocomplete for end location
          const endAutocomplete = new google.maps.places.Autocomplete(endLocationRef.current, {
            types: ['geocode', 'establishment'],
            componentRestrictions: { country: 'IN' } // Restrict to India
          });

          // Handle place selection for start location
          startAutocomplete.addListener('place_changed', () => {
            try {
              const place = startAutocomplete.getPlace();
              if (place.formatted_address) {
                setFormData(prev => ({
                  ...prev,
                  startLocation: place.formatted_address
                }));
              }
            } catch (error) {
              console.error('Error handling start location selection:', error);
            }
          });

          // Handle place selection for end location
          endAutocomplete.addListener('place_changed', () => {
            try {
              const place = endAutocomplete.getPlace();
              if (place.formatted_address) {
                setFormData(prev => ({
                  ...prev,
                  endLocation: place.formatted_address
                }));
              }
            } catch (error) {
              console.error('Error handling end location selection:', error);
            }
          });
        } else {
          console.warn('Google Places API not fully loaded yet');
        }
      } catch (error) {
        console.error('Error initializing autocomplete:', error);
      }
    };

    if (window.google && window.google.maps && window.google.maps.places) {
      initAutocomplete();
    } else {
      const timer = setInterval(() => {
        if (window.google && window.google.maps && window.google.maps.places) {
          initAutocomplete();
          clearInterval(timer);
        }
      }, 100);
      
      // Timeout after 10 seconds
      const timeout = setTimeout(() => {
        clearInterval(timer);
        console.warn('Google Places API failed to load within timeout');
      }, 10000);
      
      return () => {
        clearInterval(timer);
        clearTimeout(timeout);
      };
    }
  }, []);

  // Load partners from Firestore or Google Places
  useEffect(() => {
    const loadPartners = async () => {
      try {
        setLoading(true);
        
        if (useGooglePlaces) {
          // Use Google Places API - no database needed
          setPartners([]);
          setFilteredPartners([]);
          setAvailableCategories([
            // Automotive Industry
            'car_dealer', 'car_repair', 'car_wash', 'gas_station', 
            'auto_parts_store', 'car_rental', 'parking', 'car_insurance_agency',
            'towing_service', 'auto_body_shop',
            
            // Retail & Shopping
            'department_store', 'clothing_store', 'shoe_store', 'jewelry_store',
            'electronics_store', 'furniture_store', 'home_goods_store', 'hardware_store',
            'book_store', 'sporting_goods_store', 'toy_store', 'pet_store',
            'pharmacy', 'convenience_store', 'supermarket', 'grocery_store',
            'shopping_mall', 'store',
            
            // Food & Beverage
            'restaurant', 'cafe', 'bakery', 'bar', 'night_club', 'food',
            'meal_takeaway', 'meal_delivery',
            
            // Professional Services
            'lawyer', 'accounting', 'real_estate_agency', 'insurance_agency',
            'bank', 'dentist', 'doctor', 'hospital', 'veterinary_care',
            
            // Beauty & Personal Care
            'beauty_salon', 'hair_care', 'spa', 'gym', 'fitness_center',
            
            // Hotels & Accommodation
            'lodging', 'hotel', 'motel', 'campground',
            
            // Entertainment & Recreation
            'movie_theater', 'bowling_alley', 'amusement_park', 'aquarium',
            'art_gallery', 'museum', 'library', 'stadium', 'park',
            
            // Education
            'school', 'university',
            
            // Transportation
            'subway_station', 'train_station', 'bus_station', 'airport', 'taxi_stand',
            
            // Religious
            'church', 'mosque', 'synagogue', 'hindu_temple',
            
            // Government & Public Services
            'post_office', 'police', 'fire_station', 'city_hall', 'courthouse',
            
            // General Business
            'establishment', 'point_of_interest', 'business'
          ]);
        } else {
          // Use Firestore database
          const partnersData = await fieldDayPlannerService.getAllPartners();
          setPartners(partnersData);
          setFilteredPartners(partnersData);
          
          // Extract unique categories
          const categories = [...new Set(partnersData.map(partner => partner.dealProMasterCategory).filter(Boolean))];
          setAvailableCategories(categories.sort());
        }
      } catch (error) {
        console.error('Error loading partners:', error);
      } finally {
        setLoading(false);
      }
    };

    loadPartners();
    
    // Set default plan name
    const today = new Date();
    const dateStr = today.toLocaleDateString('en-IN', { 
      day: '2-digit', 
      month: 'short', 
      year: 'numeric' 
    });
    setPlanName(`Field Visit Plan - ${dateStr}`);
  }, [useGooglePlaces]);

  const generatePlanName = (formData) => {
    const today = new Date();
    const dateStr = today.toLocaleDateString('en-IN', { 
      day: '2-digit', 
      month: 'short', 
      year: 'numeric' 
    });
    
    const startCity = formData.startLocation.split(',')[0].trim();
    const endCity = formData.endLocation.split(',')[0].trim();
    
    let suggestedName = `Field Visit - ${startCity} to ${endCity} - ${dateStr}`;
    
    // If waypoints are provided, include them
    if (formData.waypoints.trim()) {
      const waypointCities = formData.waypoints
        .split(',')
        .map(wp => wp.trim().split(',')[0])
        .filter(city => city.length > 0)
        .slice(0, 2); // Limit to first 2 waypoints to keep name manageable
      
      if (waypointCities.length > 0) {
        suggestedName = `Field Visit - ${startCity} via ${waypointCities.join(', ')} to ${endCity} - ${dateStr}`;
      }
    }
    
    return suggestedName;
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));

    // Generate plan name suggestion when start/end locations change
    if (field === 'startLocation' || field === 'endLocation' || field === 'waypoints') {
      const updatedFormData = { ...formData, [field]: value };
      if (updatedFormData.startLocation && updatedFormData.endLocation) {
        const suggestedName = generatePlanName(updatedFormData);
        setPlanName(suggestedName);
      }
    }
  };

  const handleCategoryToggle = (category) => {
    setSelectedCategories(prev => {
      if (prev.includes(category)) {
        return prev.filter(cat => cat !== category);
      } else {
        return [...prev, category];
      }
    });
  };

  const handleSelectAllCategories = () => {
    setSelectedCategories(availableCategories);
  };

  const handleClearAllCategories = () => {
    setSelectedCategories([]);
  };

  const clearAllParameters = () => {
    // Reset form data
    setFormData({
      startLocation: '',
      endLocation: '',
      waypoints: '',
      radius: '1000'
    });
    
    // Reset other states
    setSelectedCategories([]);
    setRoute(null);
    setFilteredPartners([]);
    setCurrentPlan(null);
    setPlanName('');
    setVisitStatus({});
    setVisitNotes({});
    setCurrentPage(1);
            // Clear any previous errors
    
    // Clear map markers
    routeMarkers.forEach(marker => marker.setMap(null));
    setRouteMarkers([]);
    
    // Clear directions on map
    if (directionsRenderer) {
      directionsRenderer.setDirections({ routes: [] });
    }
    
    // Reset map to default view
    if (map) {
      map.setCenter({ lat: 20.5937, lng: 78.9629 }); // Center of India
      map.setZoom(5);
    }
  };

  const searchGooglePlaces = async (route, radiusInMeters, selectedTypes) => {
    return new Promise((resolve) => {
      if (!googlePlacesService || !route) {
        console.warn('Google Places service or route not available');
        resolve([]);
        return;
      }

      const places = [];
      const path = route.overview_path;
      
      try {
        if (!path || !path.getLength) {
          // Fallback: search around start and end points
          const searchPoints = [
            route.legs[0].start_location,
            route.legs[0].end_location
          ];
          
          let completedSearches = 0;
          searchPoints.forEach((point, index) => {
            // Add delay to avoid rate limiting
            setTimeout(() => {
              const request = {
                location: point,
                radius: radiusInMeters,
                type: selectedTypes.length > 0 ? selectedTypes[0] : undefined // Use first type only
              };
              
              googlePlacesService.nearbySearch(request, (results, status) => {
                try {
                  if (status === google.maps.places.PlacesServiceStatus.OK && results) {
                    results.forEach(place => {
                      places.push({
                        id: place.place_id,
                        dealProMasterName: place.name || 'Unknown',
                        dealProMasterCategory: place.types?.[0] || 'establishment',
                        dealProMasterAddressFull: place.vicinity || '',
                        dealProMasterLatitude: place.geometry?.location?.lat() || 0,
                        dealProMasterLongitude: place.geometry?.location?.lng() || 0,
                        dealProMasterGoogleRating: place.rating || 0,
                        dealProMasterGoogleReviewsCount: place.user_ratings_total || 0,
                        dealProMasterPhoneNumber: place.formatted_phone_number || 'N/A',
                        dealProMasterWebsite: place.website || null
                      });
                    });
                  } else if (status === google.maps.places.PlacesServiceStatus.OVER_QUERY_LIMIT) {
                    console.warn('Google Places API rate limit reached');
                  } else if (status === google.maps.places.PlacesServiceStatus.REQUEST_DENIED) {
                    console.warn('Google Places API request denied');
                  } else {
                    console.warn(`Google Places search failed for point ${index}:`, status);
                  }
                } catch (error) {
                  console.error('Error processing Google Places results:', error);
                }
                
                completedSearches++;
                if (completedSearches === searchPoints.length) {
                  resolve(places);
                }
              });
            }, index * 300); // 300ms delay between requests
          });
        } else {
          // Search along route path (limited to avoid API limits)
          const searchPoints = [];
          const step = Math.max(1, Math.floor(path.getLength() / 6)); // Reduce to 6 searches to avoid rate limits
          for (let i = 0; i < path.getLength(); i += step) {
            searchPoints.push(path.getAt(i));
          }
          
          let completedSearches = 0;
          searchPoints.forEach((point, index) => {
            // Add delay to avoid rate limiting
            setTimeout(() => {
              const request = {
                location: point,
                radius: radiusInMeters,
                type: selectedTypes.length > 0 ? selectedTypes[0] : undefined // Use first type only
              };
              
              googlePlacesService.nearbySearch(request, (results, status) => {
                try {
                  if (status === google.maps.places.PlacesServiceStatus.OK && results) {
                    results.forEach(place => {
                      // Check if place already exists
                      if (!places.find(p => p.id === place.place_id)) {
                        places.push({
                          id: place.place_id,
                          dealProMasterName: place.name || 'Unknown',
                          dealProMasterCategory: place.types?.[0] || 'establishment',
                          dealProMasterAddressFull: place.vicinity || '',
                          dealProMasterLatitude: place.geometry?.location?.lat() || 0,
                          dealProMasterLongitude: place.geometry?.location?.lng() || 0,
                          dealProMasterGoogleRating: place.rating || 0,
                          dealProMasterGoogleReviewsCount: place.user_ratings_total || 0,
                          dealProMasterPhoneNumber: place.formatted_phone_number || 'N/A',
                          dealProMasterWebsite: place.website || null
                        });
                      }
                    });
                  } else if (status === google.maps.places.PlacesServiceStatus.OVER_QUERY_LIMIT) {
                    console.warn('Google Places API rate limit reached');
                  } else if (status === google.maps.places.PlacesServiceStatus.REQUEST_DENIED) {
                    console.warn('Google Places API request denied');
                  } else {
                    console.warn(`Google Places search failed for point ${index}:`, status);
                  }
                } catch (error) {
                  console.error('Error processing Google Places results:', error);
                }
                
                completedSearches++;
                if (completedSearches === searchPoints.length) {
                  resolve(places);
                }
              });
            }, index * 300); // 300ms delay between requests
          });
        }
      } catch (error) {
        console.error('Error in searchGooglePlaces:', error);
        resolve([]);
      }
    });
  };

  const generatePlan = async () => {
    if (!formData.startLocation || !formData.endLocation) {
      alert('Please enter start and end locations');
      return;
    }

    // Validate that we have valid addresses
    if (formData.startLocation.trim().length < 3 || formData.endLocation.trim().length < 3) {
      alert('Please enter valid addresses (at least 3 characters)');
      return;
    }

    try {
      setLoading(true);

      // Generate suggested plan name
      const suggestedName = generatePlanName(formData);
      setPlanName(suggestedName);

      // Create waypoints array
      const waypoints = formData.waypoints
        .split(',')
        .map(wp => wp.trim())
        .filter(wp => wp.length > 0)
        .map(wp => ({ location: wp }));

      // Get directions
      const request = {
        origin: formData.startLocation,
        destination: formData.endLocation,
        waypoints: waypoints,
        optimizeWaypoints: true,
        travelMode: google.maps.TravelMode.DRIVING
      };

      const result = await directionsService.route(request);
      directionsRenderer.setDirections(result);
      
      const route = result.routes[0];
      setRoute(route);

      // Get partners based on data source
      const radiusInMeters = parseInt(formData.radius);
      let nearbyPartners = [];

      if (useGooglePlaces) {
        // Use Google Places API
        try {
          nearbyPartners = await searchGooglePlaces(route, radiusInMeters, selectedCategories);
          if (nearbyPartners.length === 0) {
            console.warn('No places found via Google Places API, falling back to database');
            // Fallback to database if no places found
            nearbyPartners = partners.filter(partner => {
              const partnerPoint = {
                lat: parseFloat(partner.dealProMasterLatitude),
                lng: parseFloat(partner.dealProMasterLongitude)
              };

              const startPoint = route.legs[0].start_location;
              const endPoint = route.legs[0].end_location;
              
              const distanceFromStart = google.maps.geometry.spherical.computeDistanceBetween(
                new google.maps.LatLng(partnerPoint.lat, partnerPoint.lng),
                startPoint
              );
              
              const distanceFromEnd = google.maps.geometry.spherical.computeDistanceBetween(
                new google.maps.LatLng(partnerPoint.lat, partnerPoint.lng),
                endPoint
              );
              
              return distanceFromStart <= radiusInMeters || distanceFromEnd <= radiusInMeters;
            });

            if (selectedCategories.length > 0) {
              nearbyPartners = nearbyPartners.filter(partner => 
                selectedCategories.includes(partner.dealProMasterCategory)
              );
            }
          }
        } catch (error) {
          console.error('Google Places API error:', error);
          alert('Google Places API error. Switching to database mode.');
          setUseGooglePlaces(false);
          return;
        }
      } else {
        // Use Firestore database
        nearbyPartners = partners.filter(partner => {
          const partnerPoint = {
            lat: parseFloat(partner.dealProMasterLatitude),
            lng: parseFloat(partner.dealProMasterLongitude)
          };

          // Check if partner is near any segment of the route
          const path = route.overview_path;
          if (!path || !path.getLength) {
            console.warn('Route path not available for partner filtering, using fallback method');
            // Fallback: check if partner is within radius of start or end points
            const startPoint = route.legs[0].start_location;
            const endPoint = route.legs[0].end_location;
            
            const distanceFromStart = google.maps.geometry.spherical.computeDistanceBetween(
              new google.maps.LatLng(partnerPoint.lat, partnerPoint.lng),
              startPoint
            );
            
            const distanceFromEnd = google.maps.geometry.spherical.computeDistanceBetween(
              new google.maps.LatLng(partnerPoint.lat, partnerPoint.lng),
              endPoint
            );
            
            return distanceFromStart <= radiusInMeters || distanceFromEnd <= radiusInMeters;
          }

          for (let i = 0; i < path.getLength() - 1; i++) {
            const segmentStart = path.getAt(i);
            
            const distance = google.maps.geometry.spherical.computeDistanceBetween(
              new google.maps.LatLng(partnerPoint.lat, partnerPoint.lng),
              segmentStart
            );
            
            if (distance <= radiusInMeters) {
              return true;
            }
          }
          return false;
        });

        // Filter by selected categories
        if (selectedCategories.length > 0) {
          nearbyPartners = nearbyPartners.filter(partner => 
            selectedCategories.includes(partner.dealProMasterCategory)
          );
        }
      }

      setFilteredPartners(nearbyPartners);
      setCurrentPage(1);

      // Set current plan for saving
      setCurrentPlan({
        route,
        partners: nearbyPartners,
        formData,
        selectedCategories,
        useGooglePlaces
      });

      // Clear existing markers
      routeMarkers.forEach(marker => marker.setMap(null));
      setRouteMarkers([]);

      // Add route markers (start, end, waypoints)
      const newRouteMarkers = [];
      
      // Start location marker
      const startMarker = new google.maps.Marker({
        position: route.legs[0].start_location,
        map: map,
        title: 'Start Location',
        label: 'A',
        icon: {
          url: 'http://maps.google.com/mapfiles/ms/icons/green-dot.png',
          scaledSize: new google.maps.Size(32, 32)
        }
      });
      
      const startInfoWindow = new google.maps.InfoWindow({
        content: `
          <div class="p-2">
            <h3 class="font-bold text-green-600">🚀 Start Location</h3>
            <p class="text-sm">${formData.startLocation}</p>
          </div>
        `
      });
      
      startMarker.addListener('click', () => {
        startInfoWindow.open(map, startMarker);
      });
      
      newRouteMarkers.push(startMarker);

      // End location marker
      const endMarker = new google.maps.Marker({
        position: route.legs[0].end_location,
        map: map,
        title: 'End Location',
        label: 'B',
        icon: {
          url: 'http://maps.google.com/mapfiles/ms/icons/red-dot.png',
          scaledSize: new google.maps.Size(32, 32)
        }
      });
      
      const endInfoWindow = new google.maps.InfoWindow({
        content: `
          <div class="p-2">
            <h3 class="font-bold text-red-600">🏁 End Location</h3>
            <p class="text-sm">${formData.endLocation}</p>
          </div>
        `
      });
      
      endMarker.addListener('click', () => {
        endInfoWindow.open(map, endMarker);
      });
      
      newRouteMarkers.push(endMarker);

      // Waypoint markers
      if (route.legs.length > 1) {
        route.legs.forEach((leg, index) => {
          if (index > 0) { // Skip first leg as it's the start
            const waypointMarker = new google.maps.Marker({
              position: leg.start_location,
              map: map,
              title: `Waypoint ${index}`,
              label: String.fromCharCode(67 + index - 1), // C, D, E, etc.
              icon: {
                url: 'http://maps.google.com/mapfiles/ms/icons/blue-dot.png',
                scaledSize: new google.maps.Size(28, 28)
              }
            });
            
            const waypointInfoWindow = new google.maps.InfoWindow({
              content: `
                <div class="p-2">
                  <h3 class="font-bold text-blue-600">📍 Waypoint ${index}</h3>
                  <p class="text-sm">${formData.waypoints.split(',')[index - 1]?.trim() || 'Waypoint'}</p>
                </div>
              `
            });
            
            waypointMarker.addListener('click', () => {
              waypointInfoWindow.open(map, waypointMarker);
            });
            
            newRouteMarkers.push(waypointMarker);
          }
        });
      }

      setRouteMarkers(newRouteMarkers);

      // Add markers for filtered partners
      nearbyPartners.forEach(partner => {
        const marker = new google.maps.Marker({
          position: {
            lat: parseFloat(partner.dealProMasterLatitude),
            lng: parseFloat(partner.dealProMasterLongitude)
          },
          map: map,
          title: partner.dealProMasterName,
          icon: {
            url: 'http://maps.google.com/mapfiles/ms/icons/blue-dot.png',
            scaledSize: new google.maps.Size(32, 32)
          }
        });

        const infoWindow = new google.maps.InfoWindow({
          content: `
            <div class="p-2">
              <h3 class="font-bold">${partner.dealProMasterName}</h3>
              <p class="text-sm text-blue-600">${partner.dealProMasterCategory || 'N/A'}</p>
              <p class="text-sm">${partner.dealProMasterAddressFull || 'N/A'}</p>
              ${partner.dealProMasterGoogleRating ? 
                `<p class="text-sm">⭐ ${partner.dealProMasterGoogleRating} (${partner.dealProMasterGoogleReviewsCount || 0} reviews)</p>` : 
                ''
              }
            </div>
          `
        });

        marker.addListener('click', () => {
          infoWindow.open(map, marker);
        });
      });

    } catch (error) {
      console.error('Error generating plan:', error);
      
      // Provide more specific error messages
      if (error.message && error.message.includes('NOT_FOUND')) {
        alert('Could not find a route between the specified locations. Please check your start and end addresses and try again.');
      } else if (error.message && error.message.includes('ZERO_RESULTS')) {
        alert('No route found between the specified locations. Please try different addresses.');
      } else if (error.message && error.message.includes('OVER_QUERY_LIMIT')) {
        alert('Google Places API limit reached. Please try again later or use database mode.');
      } else if (error.message && error.message.includes('REQUEST_DENIED')) {
        alert('Google Places API request denied. Please check your API key and try again.');
      } else {
        alert('Error generating route. Please check your locations and try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const updateVisitStatus = (partnerId, status) => {
    setVisitStatus(prev => ({
      ...prev,
      [partnerId]: status
    }));
  };

  const updateVisitNotes = (partnerId, notes) => {
    setVisitNotes(prev => ({
      ...prev,
      [partnerId]: notes
    }));
  };

  const savePlanToDatabase = async () => {
    if (!currentPlan || !planName.trim()) {
      alert('Please generate a plan first and provide a plan name');
      return;
    }

    try {
      // Clean partner data to remove undefined values and add additional details
      const cleanPartners = filteredPartners.map(partner => {
        const cleanPartner = {};
        Object.keys(partner).forEach(key => {
          if (partner[key] !== undefined && partner[key] !== null) {
            cleanPartner[key] = partner[key];
          } else {
            cleanPartner[key] = ''; // Replace undefined/null with empty string
          }
        });
        
        // Add visit status and notes
        cleanPartner.visitStatus = visitStatus[partner.id] || 'not_visited';
        cleanPartner.notes = visitNotes[partner.id] || '';
        
        // Add Google Maps link
        if (partner.dealProMasterLatitude && partner.dealProMasterLongitude) {
          cleanPartner.googleMapsLink = `https://www.google.com/maps?q=${partner.dealProMasterLatitude},${partner.dealProMasterLongitude}`;
        } else {
          cleanPartner.googleMapsLink = '';
        }
        
        // Add Google Places link if place_id exists (for Google Places API results)
        if (partner.place_id) {
          cleanPartner.googlePlacesLink = `https://www.google.com/maps/place/?q=place_id:${partner.place_id}`;
        } else {
          cleanPartner.googlePlacesLink = '';
        }
        
        // Ensure phone number is properly formatted
        if (partner.dealProMasterPhoneNumber && partner.dealProMasterPhoneNumber !== 'N/A') {
          cleanPartner.formattedPhone = partner.dealProMasterPhoneNumber;
        } else {
          cleanPartner.formattedPhone = '';
        }
        
        // Add partner details for email
        cleanPartner.partnerDetails = {
          name: partner.dealProMasterName || 'Unknown',
          category: partner.dealProMasterCategory || 'N/A',
          address: partner.dealProMasterAddressFull || 'N/A',
          city: partner.dealProMasterCity || 'N/A',
          state: partner.dealProMasterState || 'N/A',
          phone: cleanPartner.formattedPhone,
          rating: partner.dealProMasterGoogleRating || 0,
          reviews: partner.dealProMasterGoogleReviewsCount || 0,
          website: partner.dealProMasterWebsite || '',
          googleMapsLink: cleanPartner.googleMapsLink,
          googlePlacesLink: cleanPartner.googlePlacesLink
        };
        
        return cleanPartner;
      });

      const planData = {
        name: planName,
        createdAt: new Date().toISOString(),
        createdBy: 'admin', // You can get this from auth context
        startLocation: formData.startLocation || '',
        endLocation: formData.endLocation || '',
        waypoints: formData.waypoints || '',
        radius: formData.radius || '1000',
        route: {
          distance: route?.legs?.[0]?.distance?.text || '',
          duration: route?.legs?.[0]?.duration?.text || '',
          overviewPolyline: route?.overview_polyline || ''
        },
        partners: cleanPartners,
        selectedCategories: selectedCategories || [],
        dataSource: useGooglePlaces ? 'google_places' : 'database',
        status: 'active',
        // Additional metadata for email and reporting
        summary: {
          totalPartners: cleanPartners.length,
          totalDistance: route?.legs?.[0]?.distance?.text || '',
          totalDuration: route?.legs?.[0]?.duration?.text || '',
          categories: selectedCategories || [],
          dataSource: useGooglePlaces ? 'google_places' : 'database'
        },
        // Partner statistics for email
        partnerStats: {
          byCategory: cleanPartners.reduce((acc, partner) => {
            const category = partner.dealProMasterCategory || 'Unknown';
            acc[category] = (acc[category] || 0) + 1;
            return acc;
          }, {}),
          withPhoneNumbers: cleanPartners.filter(p => p.formattedPhone).length,
          withRatings: cleanPartners.filter(p => p.dealProMasterGoogleRating).length,
          averageRating: cleanPartners.length > 0 ? 
            (cleanPartners.reduce((sum, p) => sum + (p.dealProMasterGoogleRating || 0), 0) / cleanPartners.length).toFixed(1) : 0
        }
      };

      const planId = await fieldDayPlannerService.savePlan(planData);
      alert(`Plan "${planName}" saved successfully!`);
      return planId;
    } catch (error) {
      console.error('Error saving plan:', error);
      alert('Error saving plan. Please try again.');
    }
  };



  const copyEmailToClipboard = async () => {
    if (!currentPlan || !planName.trim()) {
      alert('Please generate a plan first and provide a plan name');
      return;
    }

    try {
      // First save the plan to database
      await savePlanToDatabase();
      
      // Then copy the beautiful plan text to clipboard
      const planText = generateBeautifulPlanText();
      await copyToClipboard(planText);
      
      alert('✨ Plan saved and copied to clipboard! You can now paste it anywhere.');
    } catch (error) {
      console.error('Error copying to clipboard:', error);
      alert('Error copying to clipboard. Please try again.');
    }
  };

  const generateBeautifulPlanText = () => {
    const planText = `
╔══════════════════════════════════════════════════════════════╗
║                    FIELD DAY PLAN                            ║
║                    ${planName.padEnd(40)} ║
╚══════════════════════════════════════════════════════════════╝

🗺️ ROUTE DETAILS
═══════════════════════════════════════════════════════════════
📍 Start Location: ${formData.startLocation}
🏁 End Location:   ${formData.endLocation}
📏 Distance:       ${route?.legs?.[0]?.distance?.text || 'N/A'}
⏱️  Duration:       ${route?.legs?.[0]?.duration?.text || 'N/A'}
🔍 Search Radius:  ${formData.radius} meters
📊 Data Source:    ${useGooglePlaces ? 'Google Places API' : 'Database'}

📋 PLAN SUMMARY
═══════════════════════════════════════════════════════════════
👥 Total Partners: ${filteredPartners.length}
🏷️ Categories:     ${selectedCategories.length > 0 ? selectedCategories.join(', ') : 'All Categories'}

${selectedCategories.length > 0 ? `
📊 CATEGORY BREAKDOWN
═══════════════════════════════════════════════════════════════
${Object.entries(filteredPartners.reduce((acc, partner) => {
  const category = partner.dealProMasterCategory || 'Unknown';
  acc[category] = (acc[category] || 0) + 1;
  return acc;
}, {})).map(([category, count]) => `• ${category}: ${count} partners`).join('\n')}
` : ''}

👥 PARTNERS ALONG ROUTE
═══════════════════════════════════════════════════════════════
${filteredPartners.map((partner, index) => `
${(index + 1).toString().padStart(2, '0')}. ${partner.dealProMasterName || 'Unknown'}
    📍 Address:  ${partner.dealProMasterAddressFull || 'N/A'}
    📞 Phone:    ${partner.dealProMasterPhoneNumber || 'N/A'}
    ⭐ Rating:   ${partner.dealProMasterGoogleRating ? `${partner.dealProMasterGoogleRating}/5 (${partner.dealProMasterGoogleReviewsCount || 0} reviews)` : 'N/A'}
    🏷️ Category: ${partner.dealProMasterCategory || 'N/A'}
    ${partner.dealProMasterLatitude && partner.dealProMasterLongitude ? `🗺️  Maps:     https://www.google.com/maps?q=${partner.dealProMasterLatitude},${partner.dealProMasterLongitude}` : ''}
    ${visitStatus[partner.id] ? `📋 Status:   ${getStatusText(visitStatus[partner.id])}` : ''}
    ${visitNotes[partner.id] ? `📝 Notes:    ${visitNotes[partner.id]}` : ''}
`).join('')}

📊 STATISTICS
═══════════════════════════════════════════════════════════════
• Partners with Phone Numbers: ${filteredPartners.filter(p => p.dealProMasterPhoneNumber && p.dealProMasterPhoneNumber !== 'N/A').length}/${filteredPartners.length}
• Partners with Ratings:       ${filteredPartners.filter(p => p.dealProMasterGoogleRating).length}/${filteredPartners.length}
• Average Rating:              ${filteredPartners.length > 0 ? (filteredPartners.reduce((sum, p) => sum + (p.dealProMasterGoogleRating || 0), 0) / filteredPartners.length).toFixed(1) : 0}/5

${Object.entries(visitStatus).length > 0 ? `
📋 VISIT STATUS SUMMARY
═══════════════════════════════════════════════════════════════
${Object.entries(visitStatus).map(([partnerId, status]) => {
  const partner = filteredPartners.find(p => p.id === partnerId);
  return `• ${partner?.dealProMasterName || 'Unknown'}: ${getStatusText(status)}`;
}).join('\n')}
` : ''}

${Object.entries(visitNotes).filter(([_, notes]) => notes).length > 0 ? `
📝 NOTES SUMMARY
═══════════════════════════════════════════════════════════════
${Object.entries(visitNotes).map(([partnerId, notes]) => {
  const partner = filteredPartners.find(p => p.id === partnerId);
  return notes ? `• ${partner?.dealProMasterName || 'Unknown'}: ${notes}` : '';
}).filter(note => note).join('\n')}
` : ''}

═══════════════════════════════════════════════════════════════
Generated on: ${new Date().toLocaleDateString()} at ${new Date().toLocaleTimeString()}
═══════════════════════════════════════════════════════════════
    `.trim();

    return planText;
  };



  const copyToClipboard = async (text) => {
    try {
      if (navigator.clipboard) {
        await navigator.clipboard.writeText(text);
      } else {
        // Fallback for older browsers
        const textarea = document.createElement('textarea');
        textarea.value = text;
        document.body.appendChild(textarea);
        textarea.select();
        document.execCommand('copy');
        document.body.removeChild(textarea);
      }
    } catch (error) {
      console.error('Error copying to clipboard:', error);
      throw error;
    }
  };

  const getStatusText = (status) => {
    const statusMap = {
      'not_visited': 'Not Visited',
      'visited': 'Visited',
      'interested': 'Interested',
      'onboarded': 'Onboarded'
    };
    return statusMap[status] || 'Not Visited';
  };



  const exportToCSV = () => {
    const headers = [
      'Partner Name',
      'Category',
      'Address',
      'City',
      'State',
      'Phone',
      'Website',
      'Google Rating',
      'Reviews Count',
      'Google Maps Link',
      'Google Places Link',
      'Visit Status',
      'Notes'
    ];

    const csvData = filteredPartners.map(partner => [
      partner.dealProMasterName || 'Unknown',
      partner.dealProMasterCategory || 'N/A',
      partner.dealProMasterAddressFull || 'N/A',
      partner.dealProMasterCity || 'N/A',
      partner.dealProMasterState || 'N/A',
      partner.dealProMasterPhoneNumber || 'N/A',
      partner.dealProMasterWebsite || 'N/A',
      partner.dealProMasterGoogleRating || 'N/A',
      partner.dealProMasterGoogleReviewsCount || 'N/A',
      partner.dealProMasterLatitude && partner.dealProMasterLongitude ? 
        `https://www.google.com/maps?q=${partner.dealProMasterLatitude},${partner.dealProMasterLongitude}` : 'N/A',
      partner.place_id ? 
        `https://www.google.com/maps/place/?q=place_id:${partner.place_id}` : 'N/A',
      visitStatus[partner.id] || 'not_visited',
      visitNotes[partner.id] || ''
    ]);

    const csvContent = [headers, ...csvData]
      .map(row => row.map(cell => `"${cell}"`).join(','))
      .join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'field-day-plan.csv';
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const openPartnerModal = (partner) => {
    setSelectedPartner(partner);
    setShowPartnerModal(true);
  };

  const closePartnerModal = () => {
    setShowPartnerModal(false);
    setSelectedPartner(null);
  };



  // Pagination
  const indexOfLastPartner = currentPage * partnersPerPage;
  const indexOfFirstPartner = indexOfLastPartner - partnersPerPage;
  const currentPartners = filteredPartners.slice(indexOfFirstPartner, indexOfLastPartner);
  const totalPages = Math.ceil(filteredPartners.length / partnersPerPage);

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  return (
    <div className="flex h-full bg-gray-50 overflow-hidden">
      {/* Left Panel - Input Section */}
      <div className="w-1/3 p-6 bg-white border-r border-gray-200 overflow-y-auto flex-shrink-0">
        <div className="mb-6">
          <button
            onClick={() => navigate('/')}
            className="flex items-center text-gray-600 hover:text-gray-900 mb-4 transition-colors"
          >
            <ArrowLeftIcon className="w-5 h-5 mr-2" />
            Back to Dashboard
          </button>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Field Day Planner</h2>
          <p className="text-gray-600">Plan your sales route and track partner visits</p>
        </div>

        <div className="space-y-4">
          {/* Start Location */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Start Location
            </label>
            <div className="relative">
              <MapPinIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                ref={startLocationRef}
                type="text"
                value={formData.startLocation}
                onChange={(e) => handleInputChange('startLocation', e.target.value)}
                placeholder="Enter start address or coordinates"
                className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>

          {/* End Location */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              End Location
            </label>
            <div className="relative">
              <MapPinIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                ref={endLocationRef}
                type="text"
                value={formData.endLocation}
                onChange={(e) => handleInputChange('endLocation', e.target.value)}
                placeholder="Enter end address or coordinates"
                className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>

          {/* Waypoints */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Waypoints (Optional)
            </label>
            <textarea
              value={formData.waypoints}
              onChange={(e) => handleInputChange('waypoints', e.target.value)}
              placeholder="Enter waypoints separated by commas"
              rows="3"
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          {/* Radius */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Search Radius
            </label>
            <select
              value={formData.radius}
              onChange={(e) => handleInputChange('radius', e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="500">500 meters</option>
              <option value="1000">1 kilometer</option>
              <option value="2000">2 kilometers</option>
              <option value="5000">5 kilometers</option>
            </select>
          </div>

          {/* Data Source Toggle */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Data Source
            </label>
            <div className="flex space-x-2">
              <button
                type="button"
                onClick={() => setUseGooglePlaces(true)}
                className={`px-3 py-2 text-sm rounded-lg transition-colors ${
                  useGooglePlaces 
                    ? 'bg-blue-600 text-white' 
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                🌐 Google Places
              </button>
              <button
                type="button"
                onClick={() => setUseGooglePlaces(false)}
                className={`px-3 py-2 text-sm rounded-lg transition-colors ${
                  !useGooglePlaces 
                    ? 'bg-blue-600 text-white' 
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                🗄️ Database
              </button>
            </div>
            <p className="text-xs text-gray-500 mt-1">
              {useGooglePlaces 
                ? 'Real-time data from Google Places API' 
                : 'Data from your Firestore database'
              }
            </p>
          </div>

          {/* Partner Categories */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Partner Categories
            </label>
            <div className="space-y-2">
              <div className="flex space-x-2">
                <button
                  type="button"
                  onClick={handleSelectAllCategories}
                  className="px-3 py-1 text-xs bg-blue-100 text-blue-700 rounded hover:bg-blue-200 transition-colors"
                >
                  Select All
                </button>
                <button
                  type="button"
                  onClick={handleClearAllCategories}
                  className="px-3 py-1 text-xs bg-gray-100 text-gray-700 rounded hover:bg-gray-200 transition-colors"
                >
                  Clear All
                </button>
              </div>
              <div className="max-h-32 overflow-y-auto border border-gray-300 rounded-lg p-2">
                {availableCategories.length > 0 ? (
                  availableCategories.map((category) => (
                    <label key={category} className="flex items-center space-x-2 py-1">
                      <input
                        type="checkbox"
                        checked={selectedCategories.includes(category)}
                        onChange={() => handleCategoryToggle(category)}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                      <span className="text-sm text-gray-700">{category}</span>
                    </label>
                  ))
                ) : (
                  <p className="text-sm text-gray-500">No categories available</p>
                )}
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex space-x-3">
            {/* Generate Plan Button */}
            <button
              onClick={generatePlan}
              disabled={loading}
              className="flex-1 bg-blue-600 text-white py-3 px-4 rounded-lg hover:bg-blue-700 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center transition-colors"
            >
              {loading ? (
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
              ) : (
                <>
                  <MagnifyingGlassIcon className="w-5 h-5 mr-2" />
                  Generate Plan
                </>
              )}
            </button>

            {/* Clear Parameters Button */}
            <button
              onClick={clearAllParameters}
              className="flex-1 bg-gray-500 text-white py-3 px-4 rounded-lg hover:bg-gray-600 focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 flex items-center justify-center transition-colors"
            >
              <ArrowPathIcon className="w-5 h-5 mr-2" />
              Clear All
            </button>
          </div>

          {/* Plan Management Buttons */}
          {filteredPartners.length > 0 && (
            <div className="space-y-3">
              <button
                onClick={exportToCSV}
                className="w-full bg-green-600 text-white py-3 px-4 rounded-lg hover:bg-green-700 focus:ring-2 focus:ring-green-500 focus:ring-offset-2 flex items-center justify-center transition-colors"
              >
                <DocumentArrowDownIcon className="w-5 h-5 mr-2" />
                Export to CSV
              </button>
              
              {/* Plan Name Input */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Plan Name
                  <span className="text-xs text-gray-500 ml-1">(Auto-suggested based on route)</span>
                </label>
                <input
                  type="text"
                  value={planName}
                  onChange={(e) => setPlanName(e.target.value)}
                  placeholder="Enter plan name (e.g., Mumbai Route - Dec 15)"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-blue-50"
                />
                <p className="text-xs text-gray-500 mt-1">
                  💡 Plan name is automatically generated based on your route. You can edit it anytime.
                </p>
              </div>

              {/* Save Plan Button */}
              <button
                onClick={savePlanToDatabase}
                disabled={!planName.trim()}
                className="w-full bg-blue-600 text-white py-3 px-4 rounded-lg hover:bg-blue-700 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center transition-colors"
              >
                💾 Save Plan
              </button>

              {/* Save & Share Section */}
              <div className="border-t pt-3">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Save & Share Plan
                </label>
                <div className="space-y-2">
                  <button
                    onClick={copyEmailToClipboard}
                    disabled={!planName.trim()}
                    className="w-full bg-green-600 text-white py-3 px-4 rounded-lg hover:bg-green-700 focus:ring-2 focus:ring-green-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center transition-colors"
                  >
                    💾 Save & Copy to Clipboard
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Route Summary */}
        {route && (
          <div className="mt-6 p-4 bg-gray-50 rounded-lg shadow-sm">
            <h3 className="text-lg font-semibold text-gray-900 mb-3">Route Summary</h3>
            <div className="space-y-2 text-sm text-gray-600">
              <div className="flex justify-between">
                <span>Total Partners:</span>
                <span className="font-medium">{filteredPartners.length}</span>
              </div>
              {selectedCategories.length > 0 && (
                <div className="flex justify-between">
                  <span>Selected Categories:</span>
                  <span className="font-medium">{selectedCategories.length}</span>
                </div>
              )}
              <div className="flex justify-between">
                <span>Distance:</span>
                <span className="font-medium">{route.legs[0].distance.text}</span>
              </div>
              <div className="flex justify-between">
                <span>Duration:</span>
                <span className="font-medium">{route.legs[0].duration.text}</span>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Right Panel - Map and Table */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Map */}
        <div className="h-1/2 bg-gray-200 p-4 flex-shrink-0 relative">
          <div ref={mapRef} className="w-full h-full rounded-lg shadow-sm" />
          
          {/* Map Legend */}
          {route && (
            <div className="absolute top-4 left-4 bg-white rounded-lg shadow-md p-3 text-xs">
              <h4 className="font-semibold mb-2">Map Legend</h4>
              <div className="space-y-1">
                <div className="flex items-center">
                  <div className="w-3 h-3 bg-green-500 rounded-full mr-2"></div>
                  <span>A - Start Location</span>
                </div>
                <div className="flex items-center">
                  <div className="w-3 h-3 bg-red-500 rounded-full mr-2"></div>
                  <span>B - End Location</span>
                </div>
                <div className="flex items-center">
                  <div className="w-3 h-3 bg-blue-500 rounded-full mr-2"></div>
                  <span>C, D, E... - Waypoints</span>
                </div>
                <div className="flex items-center">
                  <div className="w-3 h-3 bg-blue-500 rounded-full mr-2"></div>
                  <span>Blue dots - Partners</span>
                </div>
                <div className="flex items-center">
                  <div className="w-3 h-3 bg-blue-500 rounded-full mr-2"></div>
                  <span>Blue line - Route path</span>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Visit List Table */}
        <div className="flex-1 bg-white p-6 overflow-y-auto min-h-0">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-semibold text-gray-900">
              Partners Along Route ({filteredPartners.length})
            </h3>
          </div>

          {filteredPartners.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              {loading ? (
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
              ) : (
                <p>No partners found along the route. Try adjusting the radius or route.</p>
              )}
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Partner
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Category
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Address
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Contact
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Rating
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Status
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {currentPartners.map((partner) => (
                      <tr 
                        key={partner.id} 
                        className="hover:bg-gray-50 cursor-pointer transition-colors"
                        onClick={() => openPartnerModal(partner)}
                      >
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div>
                            <div className="text-sm font-medium text-gray-900">
                              {partner.dealProMasterName}
                            </div>
                            <div className="text-sm text-gray-500">
                              {partner.dealProMasterCity}, {partner.dealProMasterState}
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {partner.dealProMasterCategory || 'N/A'}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-900 max-w-xs truncate">
                          {partner.dealProMasterAddressFull || 'N/A'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {partner.dealProMasterPhoneNumber || 'N/A'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {partner.dealProMasterGoogleRating ? (
                            <div className="flex items-center">
                              <StarIcon className="w-4 h-4 text-yellow-400 mr-1" />
                              <span className="text-sm text-gray-900">
                                {partner.dealProMasterGoogleRating}
                              </span>
                              <span className="text-xs text-gray-500 ml-1">
                                ({partner.dealProMasterGoogleReviewsCount || 0})
                              </span>
                            </div>
                          ) : (
                            <span className="text-sm text-gray-500">N/A</span>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <select
                            value={visitStatus[partner.id] || ''}
                            onChange={(e) => updateVisitStatus(partner.id, e.target.value)}
                            onClick={(e) => e.stopPropagation()}
                            className="text-sm border border-gray-300 rounded px-2 py-1 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          >
                            <option value="">Not Visited</option>
                            <option value="visited">Visited</option>
                            <option value="interested">Interested</option>
                            <option value="onboarded">Onboarded</option>
                          </select>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="mt-4 flex justify-center">
                  <nav className="flex space-x-2">
                    <button
                      onClick={() => paginate(currentPage - 1)}
                      disabled={currentPage === 1}
                      className="px-3 py-2 text-sm border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Previous
                    </button>
                    {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                      <button
                        key={page}
                        onClick={() => paginate(page)}
                        className={`px-3 py-2 text-sm border rounded-md ${
                          currentPage === page
                            ? 'bg-blue-600 text-white border-blue-600'
                            : 'border-gray-300 hover:bg-gray-50'
                        }`}
                      >
                        {page}
                      </button>
                    ))}
                    <button
                      onClick={() => paginate(currentPage + 1)}
                      disabled={currentPage === totalPages}
                      className="px-3 py-2 text-sm border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Next
                    </button>
                  </nav>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Partner Detail Modal */}
      {showPartnerModal && selectedPartner && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-start mb-4">
                <div className="flex items-start space-x-4 flex-1">
                  {/* Partner Image */}
                  {selectedPartner.dealProMasterImageSource && (
                    <div className="flex-shrink-0">
                      <img
                        src={selectedPartner.dealProMasterImageSource}
                        alt={selectedPartner.dealProMasterName}
                        className="w-16 h-16 rounded-lg object-cover border border-gray-200"
                        onError={(e) => {
                          e.target.style.display = 'none';
                          console.warn('Failed to load partner image:', selectedPartner.dealProMasterImageSource);
                        }}
                        onLoad={(e) => {
                          e.target.style.display = 'block';
                        }}
                        loading="lazy"
                      />
                    </div>
                  )}
                  <div className="flex-1">
                    <h2 className="text-xl font-semibold text-gray-900">
                      {selectedPartner.dealProMasterName}
                    </h2>
                    {selectedPartner.dealProMasterCategory && (
                      <p className="text-sm text-gray-500 mt-1">
                        {selectedPartner.dealProMasterCategory}
                      </p>
                    )}
                  </div>
                </div>
                <button
                  onClick={closePartnerModal}
                  className="text-gray-400 hover:text-gray-600 transition-colors flex-shrink-0"
                >
                  <XMarkIcon className="w-6 h-6" />
                </button>
              </div>

              <div className="space-y-4">
                {/* Partner Image (Large) */}
                {selectedPartner.dealProMasterImageSource && (
                  <div>
                    <label className="text-sm font-medium text-gray-500 mb-2 block">Partner Image</label>
                    <div className="flex justify-center">
                      <img
                        src={selectedPartner.dealProMasterImageSource}
                        alt={selectedPartner.dealProMasterName}
                        className="w-48 h-32 rounded-lg object-cover border border-gray-200 shadow-sm"
                        onError={(e) => {
                          e.target.style.display = 'none';
                          console.warn('Failed to load partner image:', selectedPartner.dealProMasterImageSource);
                        }}
                        onLoad={(e) => {
                          e.target.style.display = 'block';
                        }}
                        loading="lazy"
                      />
                    </div>
                  </div>
                )}

                {/* Category */}
                <div>
                  <label className="text-sm font-medium text-gray-500">Category</label>
                  <p className="text-sm text-gray-900">{selectedPartner.dealProMasterCategory || 'N/A'}</p>
                </div>

                {/* Address */}
                <div>
                  <label className="text-sm font-medium text-gray-500">Full Address</label>
                  <p className="text-sm text-gray-900">{selectedPartner.dealProMasterAddressFull || 'N/A'}</p>
                </div>

                {/* Location Details */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-gray-500">City</label>
                    <p className="text-sm text-gray-900">{selectedPartner.dealProMasterCity || 'N/A'}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-500">State</label>
                    <p className="text-sm text-gray-900">{selectedPartner.dealProMasterState || 'N/A'}</p>
                  </div>
                </div>

                {/* Contact Information */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-gray-500 flex items-center">
                      <PhoneIcon className="w-4 h-4 mr-1" />
                      Phone Number
                    </label>
                    <p className="text-sm text-gray-900">{selectedPartner.dealProMasterPhoneNumber || 'N/A'}</p>
                  </div>
                  {selectedPartner.dealProMasterWebsite && (
                    <div>
                      <label className="text-sm font-medium text-gray-500 flex items-center">
                        <GlobeAltIcon className="w-4 h-4 mr-1" />
                        Website
                      </label>
                      <a
                        href={selectedPartner.dealProMasterWebsite}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-sm text-blue-600 hover:text-blue-800 underline"
                      >
                        Visit Website
                      </a>
                    </div>
                  )}
                </div>

                {/* Google Rating */}
                {selectedPartner.dealProMasterGoogleRating && (
                  <div>
                    <label className="text-sm font-medium text-gray-500 flex items-center">
                      <StarIcon className="w-4 h-4 mr-1" />
                      Google Rating
                    </label>
                    <div className="flex items-center">
                      <span className="text-sm text-gray-900 mr-2">
                        {selectedPartner.dealProMasterGoogleRating}
                      </span>
                      <span className="text-xs text-gray-500">
                        ({selectedPartner.dealProMasterGoogleReviewsCount || 0} reviews)
                      </span>
                    </div>
                  </div>
                )}

                {/* Coordinates */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-gray-500 flex items-center">
                      <MapIcon className="w-4 h-4 mr-1" />
                      Latitude
                    </label>
                    <p className="text-sm text-gray-900">{selectedPartner.dealProMasterLatitude}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-500 flex items-center">
                      <MapIcon className="w-4 h-4 mr-1" />
                      Longitude
                    </label>
                    <p className="text-sm text-gray-900">{selectedPartner.dealProMasterLongitude}</p>
                  </div>
                </div>

                {/* Visit Status */}
                <div>
                  <label className="text-sm font-medium text-gray-500">Visit Status</label>
                  <select
                    value={visitStatus[selectedPartner.id] || ''}
                    onChange={(e) => updateVisitStatus(selectedPartner.id, e.target.value)}
                    className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="">Not Visited</option>
                    <option value="visited">Visited</option>
                    <option value="interested">Interested</option>
                    <option value="onboarded">Onboarded</option>
                  </select>
                </div>

                {/* Notes */}
                <div>
                  <label className="text-sm font-medium text-gray-500">Notes</label>
                  <textarea
                    value={visitNotes[selectedPartner.id] || ''}
                    onChange={(e) => updateVisitNotes(selectedPartner.id, e.target.value)}
                    placeholder="Add notes for this partner..."
                    rows="3"
                    className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
              </div>

              <div className="mt-6 flex justify-end">
                <button
                  onClick={closePartnerModal}
                  className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 transition-colors"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FieldDayPlanner; 